import _ from 'lodash';
import ImageFilters from '../imageFilters';
import { createFabricCanvas, createFabricTextObject, deleteFabricCanvas } from './fabricJsHelpers';

/**
 * Calculate the maximal image size for an image to fit into containerWidth & containerHeight
 * while keeping aspect ratio
 * @param imgWidth
 * @param imgHeight
 * @param containerWidth
 * @param containerHeight
 * @returns {{width: *, height: *}}
 */
export function calculateMaxImgSizeToFitContainer(
    imgWidth,
    imgHeight,
    containerWidth,
    containerHeight
) {
    let heightDelta, widthDelta;
    let resultHeight = imgHeight;
    let resultWidth = imgWidth;

    if (resultHeight > containerHeight) {
        heightDelta = imgHeight - containerHeight;
        widthDelta = (imgWidth * heightDelta) / imgHeight;

        resultWidth = imgWidth - widthDelta;
        resultHeight = containerHeight;
    }

    if (resultWidth > containerWidth) {
        widthDelta = imgWidth - containerWidth;
        heightDelta = (imgHeight * widthDelta) / imgWidth;

        resultWidth = containerWidth;
        resultHeight = imgHeight - heightDelta;
    }

    return {
        width: resultWidth,
        height: resultHeight,
    };
}

/**
 * Takes an textObject and generates and returns an imageObject
 * @param textObj
 * @param id
 * @returns {{src: string, width: number, height: number, id: *}}
 */
export function generateImageFromText(textObj, id) {
    return new Promise((resolve, reject) => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.id = `temporary-canvas-${id}`;
        document.body.appendChild(tempCanvas);

        const fabricCanvas = createFabricCanvas(tempCanvas.id, 900, 220);

        const options = {
            // center position
            originX: 'center',
            originY: 'center',
            left: 450,
            top: 110,
            // text styles
            fontFamily: textObj.font,
            fontWeight: textObj.bold ? 'bold' : 'normal',
            fontStyle: textObj.italic ? 'italic' : 'normal',
            textAlign: textObj.alignment || 'left',
            fill: `rgb(${textObj.color.r}, ${textObj.color.g}, ${textObj.color.b})`,
        };

        createFabricTextObject(textObj.text, options).then(fabricTextObject => {
            const { width, height } = calculateMaxImgSizeToFitContainer(
                fabricTextObject.width,
                fabricTextObject.height,
                tempCanvas.width,
                tempCanvas.height
            );

            fabricTextObject.scaleToWidth(width);
            fabricTextObject.scaleToHeight(height);

            fabricCanvas.add(fabricTextObject).renderAll();

            const image = {
                src: tempCanvas.toDataURL(),
                width: tempCanvas.width,
                height: tempCanvas.height,
                id,
            };

            deleteFabricCanvas(fabricCanvas);
            document.body.removeChild(tempCanvas);

            resolve(image);
        });
    });
}

/**
 * Takes an image object including width, height and src (base64),
 * rotates it by deg (currently only supports 90deg-steps),
 * returns a promise which returns an image object on resolve
 * @param image
 * @param deg
 * @param rotateFilteredSrc
 * @returns {Promise}
 */
export function rotateImage(image, deg) {
    return new Promise((resolve, reject) => {
        const newImage = _.cloneDeep(image);
        const canvas = document.createElement('canvas');
        let context = canvas.getContext('2d');

        if (deg === 90 || deg === 270) {
            newImage.width = image.height;
            newImage.height = image.width;
        }

        canvas.width = newImage.width;
        canvas.height = newImage.height;

        const imgObj = new Image();
        imgObj.src = image.src;
        imgObj.onload = () => {
            // context = clearCanvasContext(context, canvas.width, canvas.height);
            context.translate(canvas.width / 2, canvas.height / 2);
            context.rotate((deg * Math.PI) / 180);
            context.drawImage(imgObj, -image.width / 2, -image.height / 2);
            newImage.src = context.canvas.toDataURL();
            document.body.removeChild(canvas);
            resolve(newImage);
        };
    });
}

/**
 * Takes width and height of a container and an image
 * calculates x and y axis to center the image within the container
 * @param containerWidth
 * @param containerHeight
 * @param imageWidth
 * @param imageHeight
 * @returns {{x: number, y: number}}
 */
export function calculateCenterPosition(containerWidth, containerHeight) {
    const x = containerWidth / 2;
    const y = containerHeight / 2;
    return { x, y };
}

/**
 * Takes a blobURL of an image and returns a promise, which
 * on resolve returns the base64 string of the image
 * @param blobURL
 * @returns {Promise}
 */
export function blobURLToBase64(blobURL) {
    // IE FIX
    if (blobURL instanceof Blob) {
        blobURL = URL.createObjectURL(blobURL);
    }

    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        document.body.appendChild(canvas);
        let context = canvas.getContext('2d');
        const img = new Image();
        img.src = blobURL;
        img.onload = () => {
            context.canvas.width = img.width;
            context.canvas.height = img.height;
            context.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL();
            document.body.removeChild(canvas);
            resolve(base64);
        };
    });
}

export function applyBrownFilterToImage(image) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const imageObj = new Image();

    canvas.width = image.width;
    canvas.height = image.height;
    imageObj.src = image.src;

    return new Promise((resolve, reject) => {
        imageObj.onload = () => {
            context.drawImage(imageObj, 0, 0);
            const imageData = context.getImageData(0, 0, image.width, image.width);
            const filtered = ImageFilters.Brown(imageData);
            context.putImageData(filtered, 0, 0);
            const brownImg = new Image();
            brownImg.src = canvas.toDataURL();
            resolve(brownImg);
        };
    });
}
